/**
 * Generato automaticamente.
 * OpenAPI
 */
import { useMutation, useQuery } from 'react-query';
import type {
  MutationFunction,
  QueryFunction,
  QueryKey,
  UseMutationOptions,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';
import { apiClientRequest } from '../../../client/apiClient.ts';
import type { ErrorType } from '../../../client/apiClient.ts';
import type { GetModuliIdParams } from '../schemas/getModuliIdParams.ts';
import type { GetModuliIdSezioniParams } from '../schemas/getModuliIdSezioniParams.ts';
import type { GetModuliIdVersioniParams } from '../schemas/getModuliIdVersioniParams.ts';
import type { GetModuliParams } from '../schemas/getModuliParams.ts';
import type { GetModuliVersioneIdParams } from '../schemas/getModuliVersioneIdParams.ts';
import type { ModuloDTO } from '../schemas/moduloDTO.ts';
import type { ModuloDuplicaRequest } from '../schemas/moduloDuplicaRequest.ts';
import type { ModuloPdfDTO } from '../schemas/moduloPdfDTO.ts';
import type { ModuloPdfRequest } from '../schemas/moduloPdfRequest.ts';
import type { ModuloRequest } from '../schemas/moduloRequest.ts';
import type { ModuloSezioneDTO } from '../schemas/moduloSezioneDTO.ts';
import type { ModuloSezioneRequest } from '../schemas/moduloSezioneRequest.ts';
import type { ModuloVersioneDTO } from '../schemas/moduloVersioneDTO.ts';
import type { RiordinaSezioniRequest } from '../schemas/riordinaSezioniRequest.ts';
import type { SezioneDTO } from '../schemas/sezioneDTO.ts';
import type { SezioneRequest } from '../schemas/sezioneRequest.ts';
import type { WsProblema } from '../schemas/wsProblema.ts';
import type { WsRispostaPaginataListModuloDTO } from '../schemas/wsRispostaPaginataListModuloDTO.ts';
import type { WsRispostaPaginataListModuloSezioneDTO } from '../schemas/wsRispostaPaginataListModuloSezioneDTO.ts';
import type { WsRispostaPaginataListModuloVersioneDTO } from '../schemas/wsRispostaPaginataListModuloVersioneDTO.ts';

type AwaitedInput<T> = PromiseLike<T> | T;

type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;

// eslint-disable-next-line
type SecondParameter<T extends (...args: any) => any> = T extends (
  config: any,
  args: infer P,
) => any
  ? P
  : never;

/**
 * Ottiene i dettagli di un modulo.
 * @summary Dettaglio Modulo
 */
export const getModuliId = (
  id: string,
  params?: GetModuliIdParams,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<ModuloDTO>(
    {
      url: `/framework-cfg-domande/moduli/${id}`,
      method: 'get',
      params,
      signal,
    },
    options,
  );
};

export const getGetModuliIdQueryKey = (
  id: string,
  params?: GetModuliIdParams,
) =>
  [`/framework-cfg-domande/moduli/${id}`, ...(params ? [params] : [])] as const;

export const getGetModuliIdQueryOptions = <
  TData = Awaited<ReturnType<typeof getModuliId>>,
  TError = ErrorType<WsProblema>,
>(
  id: string,
  params?: GetModuliIdParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getModuliId>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<Awaited<ReturnType<typeof getModuliId>>, TError, TData> & {
  queryKey: QueryKey;
} => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetModuliIdQueryKey(id, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getModuliId>>> = ({
    signal,
  }) => getModuliId(id, params, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!id, ...queryOptions };
};

export type GetModuliIdQueryResult = NonNullable<
  Awaited<ReturnType<typeof getModuliId>>
>;
export type GetModuliIdQueryError = ErrorType<WsProblema>;

/**
 * @summary Dettaglio Modulo
 */
export const useGetModuliId = <
  TData = Awaited<ReturnType<typeof getModuliId>>,
  TError = ErrorType<WsProblema>,
>(
  id: string,
  params?: GetModuliIdParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getModuliId>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetModuliIdQueryOptions(id, params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Aggiorna i dati generali di un modulo, ovvero i dati che valgono attraverso differenti versioni, come il titolo.
 * @summary Modifica Modulo
 */
export const putModuliId = (
  id: string,
  moduloRequest: ModuloRequest,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<ModuloDTO>(
    {
      url: `/framework-cfg-domande/moduli/${id}`,
      method: 'put',
      headers: { 'Content-Type': 'application/json' },
      data: moduloRequest,
    },
    options,
  );
};

export const getPutModuliIdMutationOptions = <
  TError = ErrorType<WsProblema>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putModuliId>>,
    TError,
    { id: string; data: ModuloRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof putModuliId>>,
  TError,
  { id: string; data: ModuloRequest },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof putModuliId>>,
    { id: string; data: ModuloRequest }
  > = props => {
    const { id, data } = props ?? {};

    return putModuliId(id, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PutModuliIdMutationResult = NonNullable<
  Awaited<ReturnType<typeof putModuliId>>
>;
export type PutModuliIdMutationBody = ModuloRequest;
export type PutModuliIdMutationError = ErrorType<WsProblema>;

/**
 * @summary Modifica Modulo
 */
export const usePutModuliId = <
  TError = ErrorType<WsProblema>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putModuliId>>,
    TError,
    { id: string; data: ModuloRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getPutModuliIdMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Rimuove un modulo tramite cancellazione logica.
 * @summary Elimina Modulo
 */
export const deleteModuliId = (
  id: string,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<void>(
    { url: `/framework-cfg-domande/moduli/${id}`, method: 'delete' },
    options,
  );
};

export const getDeleteModuliIdMutationOptions = <
  TError = ErrorType<WsProblema>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteModuliId>>,
    TError,
    { id: string },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof deleteModuliId>>,
  TError,
  { id: string },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof deleteModuliId>>,
    { id: string }
  > = props => {
    const { id } = props ?? {};

    return deleteModuliId(id, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteModuliIdMutationResult = NonNullable<
  Awaited<ReturnType<typeof deleteModuliId>>
>;

export type DeleteModuliIdMutationError = ErrorType<WsProblema>;

/**
 * @summary Elimina Modulo
 */
export const useDeleteModuliId = <
  TError = ErrorType<WsProblema>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteModuliId>>,
    TError,
    { id: string },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getDeleteModuliIdMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Ottiene l'elenco delle sezioni di un modulo.
 * @summary Elenco Sezioni
 */
export const getModuliIdSezioni = (
  id: string,
  params?: GetModuliIdSezioniParams,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<WsRispostaPaginataListModuloSezioneDTO>(
    {
      url: `/framework-cfg-domande/moduli/${id}/sezioni`,
      method: 'get',
      params,
      signal,
    },
    options,
  );
};

export const getGetModuliIdSezioniQueryKey = (
  id: string,
  params?: GetModuliIdSezioniParams,
) =>
  [
    `/framework-cfg-domande/moduli/${id}/sezioni`,
    ...(params ? [params] : []),
  ] as const;

export const getGetModuliIdSezioniQueryOptions = <
  TData = Awaited<ReturnType<typeof getModuliIdSezioni>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  params?: GetModuliIdSezioniParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getModuliIdSezioni>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getModuliIdSezioni>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetModuliIdSezioniQueryKey(id, params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getModuliIdSezioni>>
  > = ({ signal }) => getModuliIdSezioni(id, params, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!id, ...queryOptions };
};

export type GetModuliIdSezioniQueryResult = NonNullable<
  Awaited<ReturnType<typeof getModuliIdSezioni>>
>;
export type GetModuliIdSezioniQueryError = ErrorType<unknown>;

/**
 * @summary Elenco Sezioni
 */
export const useGetModuliIdSezioni = <
  TData = Awaited<ReturnType<typeof getModuliIdSezioni>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  params?: GetModuliIdSezioniParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getModuliIdSezioni>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetModuliIdSezioniQueryOptions(id, params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Crea collegamento tra il modulo e le sezioni
 * @summary Crea collegamento Modulo - lista Sezioni
 */
export const putModuliIdSezioni = (
  id: string,
  moduloSezioneRequest: ModuloSezioneRequest[],
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<ModuloSezioneDTO[]>(
    {
      url: `/framework-cfg-domande/moduli/${id}/sezioni`,
      method: 'put',
      headers: { 'Content-Type': 'application/json' },
      data: moduloSezioneRequest,
    },
    options,
  );
};

export const getPutModuliIdSezioniMutationOptions = <
  TError = ErrorType<WsProblema>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putModuliIdSezioni>>,
    TError,
    { id: string; data: ModuloSezioneRequest[] },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof putModuliIdSezioni>>,
  TError,
  { id: string; data: ModuloSezioneRequest[] },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof putModuliIdSezioni>>,
    { id: string; data: ModuloSezioneRequest[] }
  > = props => {
    const { id, data } = props ?? {};

    return putModuliIdSezioni(id, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PutModuliIdSezioniMutationResult = NonNullable<
  Awaited<ReturnType<typeof putModuliIdSezioni>>
>;
export type PutModuliIdSezioniMutationBody = ModuloSezioneRequest[];
export type PutModuliIdSezioniMutationError = ErrorType<WsProblema>;

/**
 * @summary Crea collegamento Modulo - lista Sezioni
 */
export const usePutModuliIdSezioni = <
  TError = ErrorType<WsProblema>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putModuliIdSezioni>>,
    TError,
    { id: string; data: ModuloSezioneRequest[] },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getPutModuliIdSezioniMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Crea una nuova sezione nel modulo.
 * @summary Crea Sezione
 */
export const postModuliIdSezioni = (
  id: string,
  sezioneRequest: SezioneRequest,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<SezioneDTO>(
    {
      url: `/framework-cfg-domande/moduli/${id}/sezioni`,
      method: 'post',
      headers: { 'Content-Type': 'application/json' },
      data: sezioneRequest,
    },
    options,
  );
};

export const getPostModuliIdSezioniMutationOptions = <
  TError = ErrorType<WsProblema>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postModuliIdSezioni>>,
    TError,
    { id: string; data: SezioneRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postModuliIdSezioni>>,
  TError,
  { id: string; data: SezioneRequest },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postModuliIdSezioni>>,
    { id: string; data: SezioneRequest }
  > = props => {
    const { id, data } = props ?? {};

    return postModuliIdSezioni(id, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostModuliIdSezioniMutationResult = NonNullable<
  Awaited<ReturnType<typeof postModuliIdSezioni>>
>;
export type PostModuliIdSezioniMutationBody = SezioneRequest;
export type PostModuliIdSezioniMutationError = ErrorType<WsProblema>;

/**
 * @summary Crea Sezione
 */
export const usePostModuliIdSezioni = <
  TError = ErrorType<WsProblema>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postModuliIdSezioni>>,
    TError,
    { id: string; data: SezioneRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getPostModuliIdSezioniMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Crea un collegamento tra il modulo e la sezione passati nell'url
 * @summary Crea collegamento Modulo - Sezione
 */
export const putModuliIdSezioniIdSezione = (
  id: string,
  idSezione: string,
  moduloSezioneRequest: ModuloSezioneRequest,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<ModuloSezioneDTO>(
    {
      url: `/framework-cfg-domande/moduli/${id}/sezioni/${idSezione}`,
      method: 'put',
      headers: { 'Content-Type': 'application/json' },
      data: moduloSezioneRequest,
    },
    options,
  );
};

export const getPutModuliIdSezioniIdSezioneMutationOptions = <
  TError = ErrorType<WsProblema>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putModuliIdSezioniIdSezione>>,
    TError,
    { id: string; idSezione: string; data: ModuloSezioneRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof putModuliIdSezioniIdSezione>>,
  TError,
  { id: string; idSezione: string; data: ModuloSezioneRequest },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof putModuliIdSezioniIdSezione>>,
    { id: string; idSezione: string; data: ModuloSezioneRequest }
  > = props => {
    const { id, idSezione, data } = props ?? {};

    return putModuliIdSezioniIdSezione(id, idSezione, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PutModuliIdSezioniIdSezioneMutationResult = NonNullable<
  Awaited<ReturnType<typeof putModuliIdSezioniIdSezione>>
>;
export type PutModuliIdSezioniIdSezioneMutationBody = ModuloSezioneRequest;
export type PutModuliIdSezioniIdSezioneMutationError = ErrorType<WsProblema>;

/**
 * @summary Crea collegamento Modulo - Sezione
 */
export const usePutModuliIdSezioniIdSezione = <
  TError = ErrorType<WsProblema>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putModuliIdSezioniIdSezione>>,
    TError,
    { id: string; idSezione: string; data: ModuloSezioneRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions =
    getPutModuliIdSezioniIdSezioneMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Rimuove un moduloSezione a partire da id di Sezione e Modulo.
 * @summary Elimina ModuloSezione
 */
export const deleteModuliIdSezioniIdSezione = (
  id: string,
  idSezione: string,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<void>(
    {
      url: `/framework-cfg-domande/moduli/${id}/sezioni/${idSezione}`,
      method: 'delete',
    },
    options,
  );
};

export const getDeleteModuliIdSezioniIdSezioneMutationOptions = <
  TError = ErrorType<WsProblema>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteModuliIdSezioniIdSezione>>,
    TError,
    { id: string; idSezione: string },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof deleteModuliIdSezioniIdSezione>>,
  TError,
  { id: string; idSezione: string },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof deleteModuliIdSezioniIdSezione>>,
    { id: string; idSezione: string }
  > = props => {
    const { id, idSezione } = props ?? {};

    return deleteModuliIdSezioniIdSezione(id, idSezione, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteModuliIdSezioniIdSezioneMutationResult = NonNullable<
  Awaited<ReturnType<typeof deleteModuliIdSezioniIdSezione>>
>;

export type DeleteModuliIdSezioniIdSezioneMutationError = ErrorType<WsProblema>;

/**
 * @summary Elimina ModuloSezione
 */
export const useDeleteModuliIdSezioniIdSezione = <
  TError = ErrorType<WsProblema>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteModuliIdSezioniIdSezione>>,
    TError,
    { id: string; idSezione: string },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions =
    getDeleteModuliIdSezioniIdSezioneMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Aggiorna l'ordine delle sezioni in maniera massiva in base all'id modulo, fornendo le coppie (id, ordine) di ogni sezione presente.
 * @summary Riordina Sezioni
 */
export const putModuliIdSezioniAzioniRiordina = (
  id: string,
  riordinaSezioniRequest: RiordinaSezioniRequest,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<void>(
    {
      url: `/framework-cfg-domande/moduli/${id}/sezioni/azioni/riordina`,
      method: 'put',
      headers: { 'Content-Type': 'application/json' },
      data: riordinaSezioniRequest,
    },
    options,
  );
};

export const getPutModuliIdSezioniAzioniRiordinaMutationOptions = <
  TError = ErrorType<WsProblema>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putModuliIdSezioniAzioniRiordina>>,
    TError,
    { id: string; data: RiordinaSezioniRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof putModuliIdSezioniAzioniRiordina>>,
  TError,
  { id: string; data: RiordinaSezioniRequest },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof putModuliIdSezioniAzioniRiordina>>,
    { id: string; data: RiordinaSezioniRequest }
  > = props => {
    const { id, data } = props ?? {};

    return putModuliIdSezioniAzioniRiordina(id, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PutModuliIdSezioniAzioniRiordinaMutationResult = NonNullable<
  Awaited<ReturnType<typeof putModuliIdSezioniAzioniRiordina>>
>;
export type PutModuliIdSezioniAzioniRiordinaMutationBody =
  RiordinaSezioniRequest;
export type PutModuliIdSezioniAzioniRiordinaMutationError =
  ErrorType<WsProblema>;

/**
 * @summary Riordina Sezioni
 */
export const usePutModuliIdSezioniAzioniRiordina = <
  TError = ErrorType<WsProblema>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putModuliIdSezioniAzioniRiordina>>,
    TError,
    { id: string; data: RiordinaSezioniRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions =
    getPutModuliIdSezioniAzioniRiordinaMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Ottiene la struttura della configurazione PDF del modulo.
 * @summary Get modulo pdf
 */
export const getModuliIdPdf = (
  id: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<ModuloPdfDTO>(
    { url: `/framework-cfg-domande/moduli/${id}/pdf`, method: 'get', signal },
    options,
  );
};

export const getGetModuliIdPdfQueryKey = (id: string) =>
  [`/framework-cfg-domande/moduli/${id}/pdf`] as const;

export const getGetModuliIdPdfQueryOptions = <
  TData = Awaited<ReturnType<typeof getModuliIdPdf>>,
  TError = ErrorType<WsProblema>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getModuliIdPdf>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getModuliIdPdf>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetModuliIdPdfQueryKey(id);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getModuliIdPdf>>> = ({
    signal,
  }) => getModuliIdPdf(id, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!id, ...queryOptions };
};

export type GetModuliIdPdfQueryResult = NonNullable<
  Awaited<ReturnType<typeof getModuliIdPdf>>
>;
export type GetModuliIdPdfQueryError = ErrorType<WsProblema>;

/**
 * @summary Get modulo pdf
 */
export const useGetModuliIdPdf = <
  TData = Awaited<ReturnType<typeof getModuliIdPdf>>,
  TError = ErrorType<WsProblema>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getModuliIdPdf>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetModuliIdPdfQueryOptions(id, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Modifica la struttura della configurazione PDF del modulo.
 * @summary Modifica modulo pdf
 */
export const putModuliIdPdf = (
  id: string,
  moduloPdfRequest: ModuloPdfRequest,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<ModuloPdfDTO>(
    {
      url: `/framework-cfg-domande/moduli/${id}/pdf`,
      method: 'put',
      headers: { 'Content-Type': 'application/json' },
      data: moduloPdfRequest,
    },
    options,
  );
};

export const getPutModuliIdPdfMutationOptions = <
  TError = ErrorType<WsProblema>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putModuliIdPdf>>,
    TError,
    { id: string; data: ModuloPdfRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof putModuliIdPdf>>,
  TError,
  { id: string; data: ModuloPdfRequest },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof putModuliIdPdf>>,
    { id: string; data: ModuloPdfRequest }
  > = props => {
    const { id, data } = props ?? {};

    return putModuliIdPdf(id, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PutModuliIdPdfMutationResult = NonNullable<
  Awaited<ReturnType<typeof putModuliIdPdf>>
>;
export type PutModuliIdPdfMutationBody = ModuloPdfRequest;
export type PutModuliIdPdfMutationError = ErrorType<WsProblema>;

/**
 * @summary Modifica modulo pdf
 */
export const usePutModuliIdPdf = <
  TError = ErrorType<WsProblema>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putModuliIdPdf>>,
    TError,
    { id: string; data: ModuloPdfRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getPutModuliIdPdfMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Ottiene i dettagli dei moduli.
 * @summary Lista moduli
 */
export const getModuli = (
  params?: GetModuliParams,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<WsRispostaPaginataListModuloDTO>(
    { url: `/framework-cfg-domande/moduli`, method: 'get', params, signal },
    options,
  );
};

export const getGetModuliQueryKey = (params?: GetModuliParams) =>
  [`/framework-cfg-domande/moduli`, ...(params ? [params] : [])] as const;

export const getGetModuliQueryOptions = <
  TData = Awaited<ReturnType<typeof getModuli>>,
  TError = ErrorType<WsProblema>,
>(
  params?: GetModuliParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getModuli>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<Awaited<ReturnType<typeof getModuli>>, TError, TData> & {
  queryKey: QueryKey;
} => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetModuliQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getModuli>>> = ({
    signal,
  }) => getModuli(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions };
};

export type GetModuliQueryResult = NonNullable<
  Awaited<ReturnType<typeof getModuli>>
>;
export type GetModuliQueryError = ErrorType<WsProblema>;

/**
 * @summary Lista moduli
 */
export const useGetModuli = <
  TData = Awaited<ReturnType<typeof getModuli>>,
  TError = ErrorType<WsProblema>,
>(
  params?: GetModuliParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getModuli>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetModuliQueryOptions(params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Crea un nuovo modulo. Crea anche la prima versione del modulo.
 * @summary Crea Modulo
 */
export const postModuli = (
  moduloRequest: ModuloRequest,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<ModuloDTO>(
    {
      url: `/framework-cfg-domande/moduli`,
      method: 'post',
      headers: { 'Content-Type': 'application/json' },
      data: moduloRequest,
    },
    options,
  );
};

export const getPostModuliMutationOptions = <
  TError = ErrorType<WsProblema>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postModuli>>,
    TError,
    { data: ModuloRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postModuli>>,
  TError,
  { data: ModuloRequest },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postModuli>>,
    { data: ModuloRequest }
  > = props => {
    const { data } = props ?? {};

    return postModuli(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostModuliMutationResult = NonNullable<
  Awaited<ReturnType<typeof postModuli>>
>;
export type PostModuliMutationBody = ModuloRequest;
export type PostModuliMutationError = ErrorType<WsProblema>;

/**
 * @summary Crea Modulo
 */
export const usePostModuli = <
  TError = ErrorType<WsProblema>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postModuli>>,
    TError,
    { data: ModuloRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getPostModuliMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Duplica un Modulo. Ritorna i dati del modulo duplicato.
 * @summary Duplica Modulo
 */
export const postModuliIdAzioniDuplica = (
  id: string,
  moduloDuplicaRequest: ModuloDuplicaRequest,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<ModuloDTO>(
    {
      url: `/framework-cfg-domande/moduli/${id}/azioni/duplica`,
      method: 'post',
      headers: { 'Content-Type': 'application/json' },
      data: moduloDuplicaRequest,
    },
    options,
  );
};

export const getPostModuliIdAzioniDuplicaMutationOptions = <
  TError = ErrorType<WsProblema>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postModuliIdAzioniDuplica>>,
    TError,
    { id: string; data: ModuloDuplicaRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postModuliIdAzioniDuplica>>,
  TError,
  { id: string; data: ModuloDuplicaRequest },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postModuliIdAzioniDuplica>>,
    { id: string; data: ModuloDuplicaRequest }
  > = props => {
    const { id, data } = props ?? {};

    return postModuliIdAzioniDuplica(id, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostModuliIdAzioniDuplicaMutationResult = NonNullable<
  Awaited<ReturnType<typeof postModuliIdAzioniDuplica>>
>;
export type PostModuliIdAzioniDuplicaMutationBody = ModuloDuplicaRequest;
export type PostModuliIdAzioniDuplicaMutationError = ErrorType<WsProblema>;

/**
 * @summary Duplica Modulo
 */
export const usePostModuliIdAzioniDuplica = <
  TError = ErrorType<WsProblema>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postModuliIdAzioniDuplica>>,
    TError,
    { id: string; data: ModuloDuplicaRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getPostModuliIdAzioniDuplicaMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Ottiene l'elenco delle versioni di un modulo.
 * @summary Elenco Versioni di un Modulo
 */
export const getModuliIdVersioni = (
  id: string,
  params?: GetModuliIdVersioniParams,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<WsRispostaPaginataListModuloVersioneDTO>(
    {
      url: `/framework-cfg-domande/moduli/${id}/versioni`,
      method: 'get',
      params,
      signal,
    },
    options,
  );
};

export const getGetModuliIdVersioniQueryKey = (
  id: string,
  params?: GetModuliIdVersioniParams,
) =>
  [
    `/framework-cfg-domande/moduli/${id}/versioni`,
    ...(params ? [params] : []),
  ] as const;

export const getGetModuliIdVersioniQueryOptions = <
  TData = Awaited<ReturnType<typeof getModuliIdVersioni>>,
  TError = ErrorType<WsProblema>,
>(
  id: string,
  params?: GetModuliIdVersioniParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getModuliIdVersioni>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getModuliIdVersioni>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetModuliIdVersioniQueryKey(id, params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getModuliIdVersioni>>
  > = ({ signal }) => getModuliIdVersioni(id, params, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!id, ...queryOptions };
};

export type GetModuliIdVersioniQueryResult = NonNullable<
  Awaited<ReturnType<typeof getModuliIdVersioni>>
>;
export type GetModuliIdVersioniQueryError = ErrorType<WsProblema>;

/**
 * @summary Elenco Versioni di un Modulo
 */
export const useGetModuliIdVersioni = <
  TData = Awaited<ReturnType<typeof getModuliIdVersioni>>,
  TError = ErrorType<WsProblema>,
>(
  id: string,
  params?: GetModuliIdVersioniParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getModuliIdVersioni>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetModuliIdVersioniQueryOptions(id, params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Ottiene il dettaglio di una versione del modulo.
 * @summary Dettaglio Versione del Modulo
 */
export const getModuliIdVersioniIdVersione = (
  id: string,
  idVersione: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<ModuloVersioneDTO>(
    {
      url: `/framework-cfg-domande/moduli/${id}/versioni/${idVersione}`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetModuliIdVersioniIdVersioneQueryKey = (
  id: string,
  idVersione: string,
) => [`/framework-cfg-domande/moduli/${id}/versioni/${idVersione}`] as const;

export const getGetModuliIdVersioniIdVersioneQueryOptions = <
  TData = Awaited<ReturnType<typeof getModuliIdVersioniIdVersione>>,
  TError = ErrorType<WsProblema>,
>(
  id: string,
  idVersione: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getModuliIdVersioniIdVersione>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getModuliIdVersioniIdVersione>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetModuliIdVersioniIdVersioneQueryKey(id, idVersione);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getModuliIdVersioniIdVersione>>
  > = ({ signal }) =>
    getModuliIdVersioniIdVersione(id, idVersione, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!(id && idVersione), ...queryOptions };
};

export type GetModuliIdVersioniIdVersioneQueryResult = NonNullable<
  Awaited<ReturnType<typeof getModuliIdVersioniIdVersione>>
>;
export type GetModuliIdVersioniIdVersioneQueryError = ErrorType<WsProblema>;

/**
 * @summary Dettaglio Versione del Modulo
 */
export const useGetModuliIdVersioniIdVersione = <
  TData = Awaited<ReturnType<typeof getModuliIdVersioniIdVersione>>,
  TError = ErrorType<WsProblema>,
>(
  id: string,
  idVersione: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getModuliIdVersioniIdVersione>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetModuliIdVersioniIdVersioneQueryOptions(
    id,
    idVersione,
    options,
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Ottiene l'ultima versione del modulo di un tipo domanda fissato un idGruppoModulo
 * @summary Get ultima versione modulo di un tipo domanda e idGruppoModulo
 */
export const getModuliIdGruppoTipiDomandaIdTipoDomanda = (
  idGruppo: string,
  idTipoDomanda: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<ModuloDTO>(
    {
      url: `/framework-cfg-domande/moduli/${idGruppo}/tipi-domanda/${idTipoDomanda}`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetModuliIdGruppoTipiDomandaIdTipoDomandaQueryKey = (
  idGruppo: string,
  idTipoDomanda: string,
) =>
  [
    `/framework-cfg-domande/moduli/${idGruppo}/tipi-domanda/${idTipoDomanda}`,
  ] as const;

export const getGetModuliIdGruppoTipiDomandaIdTipoDomandaQueryOptions = <
  TData = Awaited<ReturnType<typeof getModuliIdGruppoTipiDomandaIdTipoDomanda>>,
  TError = ErrorType<WsProblema>,
>(
  idGruppo: string,
  idTipoDomanda: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getModuliIdGruppoTipiDomandaIdTipoDomanda>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getModuliIdGruppoTipiDomandaIdTipoDomanda>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetModuliIdGruppoTipiDomandaIdTipoDomandaQueryKey(
      idGruppo,
      idTipoDomanda,
    );

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getModuliIdGruppoTipiDomandaIdTipoDomanda>>
  > = ({ signal }) =>
    getModuliIdGruppoTipiDomandaIdTipoDomanda(
      idGruppo,
      idTipoDomanda,
      requestOptions,
      signal,
    );

  return {
    queryKey,
    queryFn,
    enabled: !!(idGruppo && idTipoDomanda),
    ...queryOptions,
  };
};

export type GetModuliIdGruppoTipiDomandaIdTipoDomandaQueryResult = NonNullable<
  Awaited<ReturnType<typeof getModuliIdGruppoTipiDomandaIdTipoDomanda>>
>;
export type GetModuliIdGruppoTipiDomandaIdTipoDomandaQueryError =
  ErrorType<WsProblema>;

/**
 * @summary Get ultima versione modulo di un tipo domanda e idGruppoModulo
 */
export const useGetModuliIdGruppoTipiDomandaIdTipoDomanda = <
  TData = Awaited<ReturnType<typeof getModuliIdGruppoTipiDomandaIdTipoDomanda>>,
  TError = ErrorType<WsProblema>,
>(
  idGruppo: string,
  idTipoDomanda: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getModuliIdGruppoTipiDomandaIdTipoDomanda>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetModuliIdGruppoTipiDomandaIdTipoDomandaQueryOptions(
    idGruppo,
    idTipoDomanda,
    options,
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Ottiene i dettagli di un modulo tramite l'ID di un ModuloVersione.
 * @summary Dettaglio Modulo tramite Versione Modulo ID
 */
export const getModuliVersioneId = (
  id: string,
  params?: GetModuliVersioneIdParams,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<ModuloDTO>(
    {
      url: `/framework-cfg-domande/moduli/versione/${id}`,
      method: 'get',
      params,
      signal,
    },
    options,
  );
};

export const getGetModuliVersioneIdQueryKey = (
  id: string,
  params?: GetModuliVersioneIdParams,
) =>
  [
    `/framework-cfg-domande/moduli/versione/${id}`,
    ...(params ? [params] : []),
  ] as const;

export const getGetModuliVersioneIdQueryOptions = <
  TData = Awaited<ReturnType<typeof getModuliVersioneId>>,
  TError = ErrorType<WsProblema>,
>(
  id: string,
  params?: GetModuliVersioneIdParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getModuliVersioneId>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getModuliVersioneId>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetModuliVersioneIdQueryKey(id, params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getModuliVersioneId>>
  > = ({ signal }) => getModuliVersioneId(id, params, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!id, ...queryOptions };
};

export type GetModuliVersioneIdQueryResult = NonNullable<
  Awaited<ReturnType<typeof getModuliVersioneId>>
>;
export type GetModuliVersioneIdQueryError = ErrorType<WsProblema>;

/**
 * @summary Dettaglio Modulo tramite Versione Modulo ID
 */
export const useGetModuliVersioneId = <
  TData = Awaited<ReturnType<typeof getModuliVersioneId>>,
  TError = ErrorType<WsProblema>,
>(
  id: string,
  params?: GetModuliVersioneIdParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getModuliVersioneId>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetModuliVersioneIdQueryOptions(id, params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};
